import { useCallback, useEffect, useState } from 'react';
import {
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
  usePostHog,
  useFeatureFlagEnabled as usePostHogFeatureFlagEnabled,
} from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import {
  FEATURE_FLAGS,
  FeatureFlagConfig,
  FeatureFlagKey,
  FlagInfo,
} from '@erp/shared-types';
import { useLocalStorageValue } from '@react-hookz/web';
import posthog, { type PostHog } from 'posthog-js';
import config from 'src/config';
import {
  getImitationFlag,
  getImitationFlags,
  isImitationActive,
} from 'src/api/imitationState';

function trackFlagInSentry(flag: string, value: boolean | string): void {
  const integration =
    Sentry.getClient()?.getIntegrationByName<Sentry.FeatureFlagsIntegration>(
      'FeatureFlags',
    );
  integration?.addFeatureFlag(flag, value);
}

const FEATURE_FLAG_OVERRIDES_STORAGE_KEY = 'ff_overrides';

/**
 * The imitated user's value for `flag`, wrapped so "imitating, but this flag
 * isn't in the map" stays distinguishable from "not imitating". `null` means
 * the caller should resolve the flag normally.
 *
 * While a Gently admin is imitating, the backend-evaluated map on `/auth/me`
 * is the ONLY source consulted: PostHog holds the admin's own rollout and
 * `ff_overrides` holds the admin's local toggles, and showing either would
 * defeat the point of looking at the target's screen. Both are left untouched
 * and resume the moment imitation ends.
 *
 * The backend evaluates every registered flag, so an absent key means either
 * the evaluation failed or the key isn't a registered flag. Both resolve to
 * the flag's DECLARED default — the same thing an ordinary user gets when
 * PostHog has nothing to say about a flag — never to the admin's value.
 */
function readImitationFlag(
  flag: FeatureFlagKey,
): { value: boolean | string | undefined } | null {
  if (!isImitationActive()) {
    return null;
  }
  return { value: getImitationFlag(flag) };
}

/**
 * PostHog reports a multivariate flag as its variant key, and
 * `isFeatureEnabled` counts any variant as on. Mirror that so a flag declared
 * boolean here but multivariate in PostHog doesn't read as off.
 */
function isFlagValueEnabled(value: boolean | string): boolean {
  return typeof value === 'boolean' ? value : value !== 'false';
}

type FeatureFlagOverrideEntry = {
  flag: FeatureFlagKey;
  flagInfo?: FlagInfo<FeatureFlagKey>;
};

function getStoredFeatureFlagOverrides(): FeatureFlagOverrideEntry[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const raw = window.localStorage.getItem(FEATURE_FLAG_OVERRIDES_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const useFeatureFlagOverrides = () => {
  const { value: flagsWithOverrides, set: setFlagsWithOverrides } =
    useLocalStorageValue<FeatureFlagOverrideEntry[]>(
      FEATURE_FLAG_OVERRIDES_STORAGE_KEY,
      { defaultValue: [] },
    );

  return {
    flagsWithOverrides,
    setFlagsWithOverrides,
  };
};

/**
 * Where a boolean flag's value can come from, highest precedence first. Each
 * source is read lazily so a higher one short-circuits the rest (PostHog is
 * never consulted while imitating).
 */
type BooleanFlagSources = {
  imitated: () => { value: boolean | string | undefined } | null;
  overrides: () => FeatureFlagOverrideEntry[];
  posthog: () => boolean | undefined;
};

/**
 * One precedence for every boolean read: imitation map, then a localStorage
 * override, then PostHog (which serves its persisted snapshot until `/flags`
 * answers, and keeps it when `/flags` fails), then the declared default.
 */
function resolveBooleanFlag(
  flag: FeatureFlagKey,
  defaultValue: boolean,
  sources: BooleanFlagSources,
): boolean {
  const imitated = sources.imitated();
  if (imitated) {
    return imitated.value === undefined
      ? defaultValue
      : isFlagValueEnabled(imitated.value);
  }

  const override = sources.overrides().find((f) => f.flag === flag);
  if (typeof override?.flagInfo?.enabled === 'boolean') {
    return override.flagInfo.enabled;
  }

  const enabled = sources.posthog();
  return typeof enabled === 'boolean' ? enabled : defaultValue;
}

const postHogInitListeners = new Set<() => void>();

/**
 * Called from PostHog's `loaded` option once `init` has run. `PostHogProvider`
 * runs `init` in an effect, which fires after the router's first
 * `beforeLoad`, so a cold load reaches `waitForFeatureFlags` before PostHog
 * exists.
 */
export function notifyPostHogInitialized(): void {
  for (const listener of postHogInitListeners) listener();
  postHogInitListeners.clear();
}

type FeatureFlagsClient = Pick<PostHog, 'onFeatureFlags'> & {
  __loaded?: boolean;
};

/**
 * Resolves once PostHog has loaded this visitor's flags, or after
 * `timeoutMs`, whichever comes first. A route that gates on a flag before the
 * first load would read every flag as off and redirect a visitor who has it
 * on. When PostHog is configured but `init` hasn't run yet (cold load), waits
 * for `init` first. Resolves at once when PostHog is not set up (tests, local
 * without a key).
 */
export function waitForFeatureFlags(
  client: FeatureFlagsClient = posthog,
  timeoutMs = 2000,
  postHogConfigured = Boolean(config.posthogApiKey),
): Promise<void> {
  if (!postHogConfigured && !client.__loaded) return Promise.resolve();
  return new Promise((resolve) => {
    let unsubscribe: (() => void) | undefined;
    const done = () => {
      window.clearTimeout(timer);
      postHogInitListeners.delete(subscribe);
      unsubscribe?.();
      resolve();
    };
    // Called right away when flags have already loaded.
    const subscribe = () => {
      unsubscribe = client.onFeatureFlags(done);
    };
    const timer = window.setTimeout(done, timeoutMs);
    if (client.__loaded) subscribe();
    else postHogInitListeners.add(subscribe);
  });
}

export function isBooleanFeatureFlagEnabled<Key extends FeatureFlagKey>(
  flag: Key,
  defaultValue: FeatureFlagConfig[Key] & boolean,
): boolean {
  const result = resolveBooleanFlag(flag, defaultValue, {
    imitated: () => readImitationFlag(flag),
    overrides: getStoredFeatureFlagOverrides,
    posthog: () => posthog.isFeatureEnabled(flag),
  });
  trackFlagInSentry(flag, result);
  return result;
}

/** Every registered flag, straight from the registry. */
const FEATURE_FLAG_KEYS: readonly FeatureFlagKey[] =
  Object.values(FEATURE_FLAGS);

/**
 * Every registered flag resolved to a boolean. Keyed by `FeatureFlagKey`, so
 * a lookup of an unregistered key doesn't compile and a registered key can't
 * be missing — there is no second list to keep in sync.
 */
export type FeatureFlagMap = Readonly<Record<FeatureFlagKey, boolean>>;

function buildFeatureFlagMap(
  resolve: (flag: FeatureFlagKey) => boolean,
): FeatureFlagMap {
  const map: Partial<Record<FeatureFlagKey, boolean>> = {};
  for (const flag of FEATURE_FLAG_KEYS) {
    map[flag] = resolve(flag);
  }
  // Complete by construction: the loop above visits every registered key.
  return map as FeatureFlagMap;
}

/**
 * PostHog's current value for every registered flag. `send_event: false`: a
 * bulk read to gate routes and sections is not an exposure, and reporting one
 * `$feature_flag_called` per registered flag would drown the real ones.
 */
function readPostHogFlags(
  client: PostHog,
): Partial<Record<FeatureFlagKey, boolean>> {
  const values: Partial<Record<FeatureFlagKey, boolean>> = {};
  for (const flag of FEATURE_FLAG_KEYS) {
    values[flag] = client.isFeatureEnabled(flag, { send_event: false });
  }
  return values;
}

/**
 * All registered flags as booleans (default `false`), for declarative gating
 * — route `access.featureFlag`, settings sections — where calling
 * `useFeatureFlag` once per key would mean a hand-written list. Same
 * precedence as `isBooleanFeatureFlagEnabled`, and re-renders when PostHog
 * loads flags or an override changes.
 */
export function useFeatureFlags(): FeatureFlagMap {
  const client = usePostHog();
  const { flagsWithOverrides } = useFeatureFlagOverrides();
  const [posthogFlags, setPosthogFlags] = useState(() =>
    readPostHogFlags(client),
  );

  useEffect(
    () =>
      client.onFeatureFlags(() => setPosthogFlags(readPostHogFlags(client))),
    [client],
  );

  // A new object whenever `/auth/me` replaces the imitation block.
  const imitationFlags = getImitationFlags();

  return buildFeatureFlagMap((flag) =>
    resolveBooleanFlag(flag, false, {
      imitated: () => (imitationFlags ? { value: imitationFlags[flag] } : null),
      overrides: () => flagsWithOverrides ?? [],
      posthog: () => posthogFlags[flag],
    }),
  );
}

/**
 * posthog overrides Usage:
 *  - posthog.featureFlags.overrideFeatureFlags(false) // clear all overrides
 *  - posthog.featureFlags.overrideFeatureFlags(['beta-feature']) // enable flags
 *  - posthog.featureFlags.overrideFeatureFlags({'beta-feature': 'variant'}) // set variants
 *  - posthog.featureFlags.overrideFeatureFlags({ // set both flags and payloads
 *      flags: {'beta-feature': 'variant'},
 *      payloads: { 'beta-feature': { someData: true } }
 *    })
 *  - posthog.featureFlags.overrideFeatureFlags({ // only override payloads
 *      payloads: { 'beta-feature': { someData: true } }
 *    })
 */
export function useOverrideFeatureFlags<Key extends FeatureFlagKey>() {
  const { flagsWithOverrides, setFlagsWithOverrides } =
    useFeatureFlagOverrides();

  const ph = usePostHog();

  const setFlagOverridden = useCallback(
    (flag: Key, flagInfo: Partial<FlagInfo<Key>>) => {
      setFlagsWithOverrides((prev) => {
        const existingFlag = prev?.find((f) => f.flag === flag);
        if (existingFlag && prev) {
          return prev.map((f) =>
            f.flag === flag
              ? {
                  ...f,
                  flagInfo: {
                    ...f.flagInfo,
                    ...(flagInfo.enabled !== undefined && {
                      enabled: flagInfo.enabled,
                    }),
                    ...(flagInfo.variantKey !== undefined && {
                      variantKey: flagInfo.variantKey,
                    }),
                    ...(flagInfo.payload !== undefined && {
                      payload: flagInfo.payload,
                    }),
                  } as FlagInfo<FeatureFlagKey>,
                }
              : f,
          );
        }
        return [
          ...(prev ?? []),
          { flag, flagInfo: flagInfo as FlagInfo<FeatureFlagKey> },
        ];
      });
    },
    [setFlagsWithOverrides],
  );

  const toggleFlagEnabled = useCallback(
    (flag: Key, enabled: boolean) => {
      ph.featureFlags.overrideFeatureFlags({
        flags: {
          [flag]: enabled,
        },
      });
      setFlagOverridden(flag, {
        enabled,
      });
    },
    [ph, setFlagOverridden],
  );

  const setVariant = useCallback(
    (flag: Key, variant: string | undefined) => {
      ph.featureFlags.overrideFeatureFlags({ [flag]: variant });
      setFlagOverridden(flag, {
        variantKey: variant as FeatureFlagConfig[Key] extends boolean
          ? undefined
          : string | undefined,
      });
    },
    [ph, setFlagOverridden],
  );

  const setPayload = useCallback(
    (flag: Key, payload: FeatureFlagConfig[Key]) => {
      ph.featureFlags.overrideFeatureFlags({
        payloads: { [flag]: payload },
      });
      setFlagOverridden(flag, {
        payload,
      });
    },
    [ph, setFlagOverridden],
  );

  const clear = useCallback(() => {
    ph.featureFlags.overrideFeatureFlags(false);
    setFlagsWithOverrides([]);
  }, [ph, setFlagsWithOverrides]);

  const removeOverride = useCallback(
    (flag: Key) => {
      ph.featureFlags.overrideFeatureFlags(false);
      setFlagsWithOverrides((prev = []) => prev.filter((f) => f.flag !== flag));
    },
    [ph, setFlagsWithOverrides],
  );

  return {
    clear,
    toggleFlagEnabled,
    setVariant,
    setPayload,
    removeOverride,
    flagsWithOverrides,
  };
}

// fallow-ignore-next-line complexity
export function useFeatureFlag<Key extends FeatureFlagKey>(
  flag: Key,
  defaultValue: FeatureFlagConfig[Key],
): FlagInfo<Key> {
  const isEnabled = usePostHogFeatureFlagEnabled(flag);
  const variant = useFeatureFlagVariantKey(flag);
  const payload = useFeatureFlagPayload(flag);
  const { flagsWithOverrides } = useFeatureFlagOverrides();

  // Checked after the hooks above (they must run unconditionally) but before
  // both the localStorage overrides and PostHog — see `readImitationFlag`.
  const imitated = readImitationFlag(flag);
  if (imitated) {
    if (imitated.value === undefined) {
      const enabled = typeof defaultValue === 'boolean' ? defaultValue : false;
      trackFlagInSentry(flag, enabled);
      return { enabled, variantKey: undefined, payload: defaultValue };
    }

    trackFlagInSentry(flag, imitated.value);
    return {
      enabled: isFlagValueEnabled(imitated.value),
      variantKey:
        typeof imitated.value === 'string' ? imitated.value : undefined,
      // The backend map carries flag values, not payloads, so payload-driven
      // flags fall back to their declared default while imitating.
      payload: defaultValue,
    };
  }

  const isOverridden = flagsWithOverrides?.some((f) => f.flag === flag);

  if (isOverridden) {
    const overrideInfo = (flagsWithOverrides?.find((f) => f.flag === flag)
      ?.flagInfo ?? {}) as unknown as FlagInfo<Key>;
    trackFlagInSentry(flag, overrideInfo.enabled ?? false);
    return overrideInfo;
  }

  const enabled =
    isEnabled ?? (typeof defaultValue === 'boolean' ? defaultValue : false);

  trackFlagInSentry(flag, enabled);

  return {
    enabled,
    variantKey:
      typeof defaultValue === 'boolean' || typeof variant === 'boolean'
        ? undefined
        : variant,
    payload: (payload as FeatureFlagConfig[Key]) ?? defaultValue,
  };
}
